import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { IconButton, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import EMPBanner from "../../../shared/components/EMPBanner/EMPBanner";
import PreOrderReviewModal from "../../../components/preOrders/PreOrderReviewModal";
import DealerActionsClient from "../../../clients/DealerActionsClient";
import { UserContext } from "../../../shared/context/userContext";
import { DEALER_ACTIONS } from "../../../common/Constants";
import { getStoredStatus, savePreOrderStatus } from "./preOrderStatusStore";

const PLACEHOLDER_PRIORITY_LEVEL = 19;

const PreOrderActionBanner = ({ orderId }) => {
  const { t } = useTranslation();

  const [storedStatus, setStoredStatus] = useState(() =>
    getStoredStatus(orderId)
  );
  const [isCancelledBannerDismissed, setIsCancelledBannerDismissed] =
    useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAcceptedBanner, setShowAcceptedBanner] = useState(false);
  const user = useContext(UserContext)?.user;
  const dealerCommonId = user?.commonId;

  const cancelPreOrder = useMutation({
    mutationFn: (dealerRejectedReason) =>
      DealerActionsClient.actionOnOrder(
        dealerCommonId,
        {
          reservationId: orderId,
          dealerAction: DEALER_ACTIONS.PRE_ORDER_CANCELLED,
          dealerRejectedReason,
        },
        user?.userId
      ),
  });

  const isCanceled = storedStatus?.status === "cancelled";
  const isAccepted = storedStatus?.status === "accepted";

  const handleAccept = async () => {
    await DealerActionsClient.actionOnOrder(
      dealerCommonId,
      {
        reservationId: orderId,
        dealerAction: DEALER_ACTIONS.PRE_ORDER_ACCEPTED,
      },
      user?.userId
    );
    savePreOrderStatus(orderId, "accepted");
    setStoredStatus({ status: "accepted" });
    setShowAcceptedBanner(true);
  };

  const handleCancellationSuccess = (details) => {
    savePreOrderStatus(orderId, "cancelled", details);
    setStoredStatus({ status: "cancelled", ...details });
    setIsCancelledBannerDismissed(false);
  };

  return (
    <>
      {!isCanceled && !isAccepted && (
        <EMPBanner
          severity="info"
          icon={
            <InfoOutlinedIcon aria-hidden="true" sx={{ color: "#0562d2" }} />
          }
          title={t("CustomerOrderDetails.preOrder.banner.title")}
          content={t("CustomerOrderDetails.preOrder.banner.content")}
          actionButton={{
            text: t("CustomerOrderDetails.preOrder.banner.actionButton"),
            testId: "preorder-banner-review-request",
            onClick: () => setIsModalOpen(true),
          }}
        />
      )}

      {isAccepted && showAcceptedBanner && (
        <div data-testid="preorder-accepted-banner">
          <EMPBanner
            severity="success"
            title={
              <span style={{ fontWeight: 700 }}>
                {t("CustomerOrderDetails.preOrder.banner.acceptedContent")}
              </span>
            }
            content=""
          />
        </div>
      )}

      {isCanceled && !isCancelledBannerDismissed && (
        <div
          data-testid="preorder-cancelled-banner"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            width: "100%",
            boxSizing: "border-box",
            padding: "12px 16px",
            backgroundColor: "#e5f4ff",
            color: "#333333",
            borderRadius: "8px",
          }}
        >
          <InfoOutlinedIcon
            aria-hidden="true"
            sx={{ color: "#0562d2", flexShrink: 0 }}
          />

          <Typography
            variant="subtitle1"
            component="p"
            sx={{
              flex: 1,
              margin: 0,
              padding: 0,
              fontWeight: 700,
              lineHeight: "24px",
            }}
          >
            {t("CustomerOrderDetails.preOrder.banner.cancelledContent", {
              defaultValue: "You have canceled the preorder.",
            })}
          </Typography>

          <IconButton
            aria-label="Close canceled preorder banner"
            data-testid="preorder-cancelled-banner-close"
            onClick={() => setIsCancelledBannerDismissed(true)}
            size="small"
            sx={{
              color: "#333333",
              flexShrink: 0,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      )}

      <PreOrderReviewModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAccept={handleAccept}
        onCancelOrder={(details) =>
          cancelPreOrder.mutateAsync(details.reasonCode)
        }
        onCancelSuccess={handleCancellationSuccess}
        priorityLevel={PLACEHOLDER_PRIORITY_LEVEL}
        orderId={orderId}
      />
    </>
  );
};

PreOrderActionBanner.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default PreOrderActionBanner;
